// ABA INSTRUÇÕES DE LAVAGEM da ficha do produto — cópia estrutural e visual
// da aba Tecnicidades (TechnicalitiesTab.tsx), so trocando technicalit ->
// care_instruction no contrato com o backend.
//
// Grade de chips selecionaveis (todas as ativas, GET /api/care-instructions),
// as do produto marcadas; ordem por arrastar entre os SELECIONADOS (mesma lib
// dnd-kit da aba Fotos); vinculo e BASE, vale para TODOS os canais (aviso
// visivel). Bloco recolhido "Override do canal <slug>": vazio = usa base.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, apiErrorMessage } from "../lib/api";
import type { ActiveCareInstruction, ProductDetail } from "../lib/types";
import { TechnicalityIcon } from "./TechnicalityIcon";
import { Button } from "./ui/button";
import { EmptyState } from "./ui/empty-state";
import { Skeleton } from "./ui/skeleton";
import { cn } from "../lib/utils";

interface CareInstructionsTabProps {
  product: ProductDetail;
  slug: string;
  disabled: boolean;
}

export function CareInstructionsTab({ product, slug, disabled }: CareInstructionsTabProps): JSX.Element {
  const qc = useQueryClient();
  const queryKey = ["product", slug, product.sku];

  const { data: all = [], isLoading, isError, error } = useQuery<ActiveCareInstruction[]>({
    queryKey: ["care-instructions-active"],
    // GET /api/care-instructions devolve { items: [...] }, mesmo formato de
    // GET /api/technicalities (catalog/technicalities.ts) — nao array puro.
    queryFn: async () =>
      (await api.get<{ items: ActiveCareInstruction[] }>("/api/care-instructions")).data.items,
  });

  const tech = product.care_instructions ?? { base: [], override: null, effective: [] };
  const byId = useMemo(() => new Map(all.map((t) => [t.id, t])), [all]);

  // ---- base (vinculo do produto, vale para todos os canais) ----
  const [baseIds, setBaseIds] = useState<number[]>(tech.base);
  const baseIdsKey = tech.base.join(",");
  useEffect(() => {
    setBaseIds(tech.base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseIdsKey]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const saveBaseMutation = useMutation({
    mutationFn: async (care_instruction_ids: number[]) => {
      await api.put(`/api/channels/${slug}/products/${product.sku}/care-instructions`, {
        care_instruction_ids,
      });
    },
    onSuccess: () => {
      toast.success("Instruções de lavagem salvas. Republish enfileirado em todos os canais deste produto.");
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const toggleBase = (id: number): void => {
    if (disabled) return;
    setBaseIds((curr) => (curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]));
  };

  const onDragEndBase = (e: DragEndEvent): void => {
    if (!e.over || e.over.id === e.active.id) return;
    const activeId = Number(e.active.id);
    const overId = Number(e.over.id);
    setBaseIds((curr) => {
      const oldIdx = curr.indexOf(activeId);
      const newIdx = curr.indexOf(overId);
      if (oldIdx < 0 || newIdx < 0) return curr;
      return arrayMove(curr, oldIdx, newIdx);
    });
  };

  const baseDirty =
    baseIds.length !== tech.base.length || baseIds.some((id, i) => id !== tech.base[i]);

  if (isLoading) {
    return (
      <div className="stack">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  if (isError) {
    return <EmptyState title="Erro ao carregar instruções de lavagem" description={apiErrorMessage(error)} />;
  }
  if (all.length === 0) {
    return (
      <EmptyState
        title="Nenhuma instrução de lavagem cadastrada ainda"
        description='Cadastre em "Instruções de lavagem" no menu Administração antes de vincular a este produto.'
      />
    );
  }

  return (
    <div className="stack" style={{ gap: "var(--sp-6)" }}>
      <div
        className="cluster"
        style={{
          alignItems: "flex-start",
          borderRadius: "var(--radius-md)",
          background: "var(--color-soft)",
          padding: "var(--sp-3)",
        }}
      >
        <Info className="icon" style={{ marginTop: ".125rem" }} aria-hidden />
        <div className="field__hint">
          Este vínculo é a <b className="text-ink">base do produto</b> e vale para{" "}
          <b className="text-ink">todos os canais</b>. Um canal pode ter uma lista própria
          no bloco de override abaixo.
        </div>
      </div>

      <section>
        <div className="spread" style={{ marginBottom: "var(--sp-2)" }}>
          <h3 className="row__title">Instruções de lavagem deste produto</h3>
          {baseIds.length > 0 ? (
            <span className="field__hint">arraste para reordenar</span>
          ) : null}
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndBase}>
          <SortableContext items={baseIds} strategy={rectSortingStrategy}>
            <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(14em,1fr))]">
              {baseIds.map((id) => {
                const t = byId.get(id);
                if (!t) return null;
                return (
                  <SortableChip
                    key={id}
                    id={id}
                    title={t.title}
                    iconUrl={t.icon_url}
                    selected
                    disabled={disabled}
                    onToggle={() => toggleBase(id)}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>

        {all.filter((t) => !baseIds.includes(t.id)).length > 0 ? (
          <>
            <div className="filters__group-label" style={{ marginTop: "var(--sp-4)", marginBottom: "var(--sp-2)" }}>
              Disponíveis
            </div>
            <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(14em,1fr))]">
              {all
                .filter((t) => !baseIds.includes(t.id))
                .map((t) => (
                  <Chip
                    key={t.id}
                    title={t.title}
                    iconUrl={t.icon_url}
                    selected={false}
                    disabled={disabled}
                    onToggle={() => toggleBase(t.id)}
                  />
                ))}
            </div>
          </>
        ) : null}

        <div className="mt-3 flex justify-end">
          <Button
            onClick={() => saveBaseMutation.mutate(baseIds)}
            loading={saveBaseMutation.isPending}
            disabled={disabled || !baseDirty}
          >
            Salvar
          </Button>
        </div>
      </section>

      <div className="border-t border-line" />

      <OverrideBlock
        product={product}
        slug={slug}
        all={all}
        disabled={disabled}
        onSaved={() => void qc.invalidateQueries({ queryKey })}
      />
    </div>
  );
}

function OverrideBlock({
  product,
  slug,
  all,
  disabled,
  onSaved,
}: {
  product: ProductDetail;
  slug: string;
  all: ActiveCareInstruction[];
  disabled: boolean;
  onSaved: () => void;
}): JSX.Element {
  const tech = product.care_instructions ?? { base: [], override: null, effective: [] };
  const hasOverride = tech.override !== null;
  const [open, setOpen] = useState(hasOverride);
  const [overrideIds, setOverrideIds] = useState<number[]>(tech.override ?? tech.base);
  useEffect(() => {
    setOpen(tech.override !== null);
    setOverrideIds(tech.override ?? tech.base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tech.override?.join(","), tech.base.join(",")]);

  const byId = useMemo(() => new Map(all.map((t) => [t.id, t])), [all]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const saveOverrideMutation = useMutation({
    mutationFn: async (care_instruction_ids: number[] | null) => {
      await api.put(`/api/channels/${slug}/products/${product.sku}/care-instructions/override`, {
        care_instruction_ids,
      });
    },
    onSuccess: (_d, care_instruction_ids) => {
      toast.success(
        care_instruction_ids === null
          ? `Override removido: canal "${slug}" volta a usar a base.`
          : `Override do canal "${slug}" salvo. Republish enfileirado neste canal.`,
      );
      onSaved();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const toggle = (id: number): void => {
    if (disabled) return;
    setOverrideIds((curr) => (curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]));
  };

  const onDragEnd = (e: DragEndEvent): void => {
    if (!e.over || e.over.id === e.active.id) return;
    const activeId = Number(e.active.id);
    const overId = Number(e.over.id);
    setOverrideIds((curr) => {
      const oldIdx = curr.indexOf(activeId);
      const newIdx = curr.indexOf(overId);
      if (oldIdx < 0 || newIdx < 0) return curr;
      return arrayMove(curr, oldIdx, newIdx);
    });
  };

  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="cluster"
        style={{ width: "100%", textAlign: "left" }}
      >
        {open ? (
          <ChevronDown className="icon text-ink-2" aria-hidden />
        ) : (
          <ChevronRight className="icon text-ink-2" aria-hidden />
        )}
        <h3 className="row__title">Override do canal &quot;{slug}&quot;</h3>
        <span className="chip">
          {hasOverride ? "lista própria" : "usa a base"}
        </span>
      </button>

      {open ? (
        <div className="stack" style={{ marginTop: "var(--sp-3)" }}>
          {!hasOverride ? (
            <div className="spread" style={{ flexWrap: "wrap", border: "var(--border)", borderStyle: "dashed", borderRadius: "var(--radius-md)", padding: "var(--sp-3)" }}>
              <span className="field__hint">Sem override: este canal mostra as mesmas instruções de lavagem da base.</span>
              <Button
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => saveOverrideMutation.mutate(overrideIds)}
                loading={saveOverrideMutation.isPending}
              >
                Usar lista própria neste canal
              </Button>
            </div>
          ) : (
            <>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={overrideIds} strategy={rectSortingStrategy}>
                  <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(14em,1fr))]">
                    {overrideIds.map((id) => {
                      const t = byId.get(id);
                      if (!t) return null;
                      return (
                        <SortableChip
                          key={id}
                          id={id}
                          title={t.title}
                          iconUrl={t.icon_url}
                          selected
                          disabled={disabled}
                          onToggle={() => toggle(id)}
                        />
                      );
                    })}
                    {overrideIds.length === 0 ? (
                      <div
                        className="field__hint"
                        style={{
                          gridColumn: "1 / -1",
                          border: "var(--border)",
                          borderStyle: "dashed",
                          borderRadius: "var(--radius-md)",
                          padding: "var(--sp-3)",
                          textAlign: "center",
                        }}
                      >
                        Escolha ao menos uma instrução de lavagem para este canal. Para o canal não mostrar nenhuma,
                        peça ao admin para desligar as instruções de lavagem na configuração do canal; para usar as
                        mesmas da base, clique em "Remover override".
                      </div>
                    ) : null}
                  </div>
                </SortableContext>
              </DndContext>

              {all.filter((t) => !overrideIds.includes(t.id)).length > 0 ? (
                <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(14em,1fr))]">
                  {all
                    .filter((t) => !overrideIds.includes(t.id))
                    .map((t) => (
                      <Chip
                        key={t.id}
                        title={t.title}
                        iconUrl={t.icon_url}
                        selected={false}
                        disabled={disabled}
                        onToggle={() => toggle(t.id)}
                      />
                    ))}
                </div>
              ) : null}

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  onClick={() => saveOverrideMutation.mutate(null)}
                  loading={saveOverrideMutation.isPending}
                >
                  Remover override (voltar a usar a base)
                </Button>
                <Button
                  size="sm"
                  disabled={disabled || overrideIds.length === 0}
                  title={overrideIds.length === 0 ? "Escolha ao menos uma instrução de lavagem" : undefined}
                  onClick={() => saveOverrideMutation.mutate(overrideIds)}
                  loading={saveOverrideMutation.isPending}
                >
                  Salvar override
                </Button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}

function Chip({
  title,
  iconUrl,
  selected,
  disabled,
  onToggle,
}: {
  title: string;
  iconUrl: string | null;
  selected: boolean;
  disabled?: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        "tech-chip",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        selected && "tech-chip--selected",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <TechnicalityIcon iconUrl={iconUrl} title={title} size="sm" />
      <span className="truncate">{title}</span>
    </button>
  );
}

function SortableChip({
  id,
  title,
  iconUrl,
  selected,
  disabled,
  onToggle,
}: {
  id: number;
  title: string;
  iconUrl: string | null;
  selected: boolean;
  disabled?: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    touchAction: "none" as const,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Chip title={title} iconUrl={iconUrl} selected={selected} disabled={disabled} onToggle={onToggle} />
    </div>
  );
}
